import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Mail, Phone, ArrowRight, Search, Handshake, FileText } from "lucide-react";
import { MaskLines, Reveal } from "../components/site/Motion";
import { SourcingForm } from "../components/site/SourcingForm";
import { AssetTypeGrid, AvailableBadge, scrollToRequest } from "../components/site/SourcingService";
import { OpportunityForm } from "./Opportunities";
import { DivisionReviews } from "../components/site/DivisionReviews";
import { DIVISIONS, http, telHref } from "../lib/api";
import { useSettings } from "../lib/settings";
import Inventory from "./Inventory";
import { NotFound } from "./Legal";

export default function DivisionPage() {
  const { key } = useParams();
  return <DivisionView key={key} divKey={key} />;
}

const PARTNER = {
  truck: ["Truck dealer and supplier partnerships", "Commercial fleet opportunities", "Truck procurement contracts", "Truck-related business proposals"],
  trailer: ["Trailer dealer and manufacturer partnerships", "Trailer supply opportunities", "Trailer procurement contracts", "Trailer-related business proposals"],
  marine: ["Marine dealer and broker partnerships", "Boat and yacht supplier opportunities", "Marine procurement contracts", "Marine-related business proposals"],
  equipment: ["Equipment dealer and supplier partnerships", "Equipment procurement opportunities", "Construction and industrial equipment contracts", "Equipment-related business proposals"],
};

const scrollToPartner = () => document.getElementById("partner-form")?.scrollIntoView({ behavior: "smooth", block: "start" });

const TwoPaths = ({ d, onPartner }) => (
  <section className="max-w-7xl mx-auto px-5 md:px-10 pt-20 md:pt-24" data-testid="division-two-paths">
    <div className="grid md:grid-cols-2 gap-px bg-white/10 border hairline">
      <div className="bg-[var(--ink)] p-8 md:p-10 flex flex-col gap-6" data-testid="division-path-asset">
        <Search className="w-7 h-7 text-gold" />
        <div>
          <p className="eyebrow">For buyers</p>
          <h2 className="font-display text-3xl md:text-4xl text-slate-50 mt-3 leading-tight">Looking for an Asset?</h2>
          <p className="text-slate-300 mt-4 leading-relaxed">Tell us what you need. We'll help you explore suitable sourcing opportunities for {d.key === "marine" ? "boats and yachts" : `${d.short.toLowerCase()} assets`} based on your requirements, specifications and budget.</p>
        </div>
        <div className="mt-auto"><button type="button" onClick={scrollToRequest} className="btn-gold" data-testid="division-path-request-button">Request an Asset <ArrowRight className="w-4 h-4" /></button></div>
      </div>
      <div className="bg-[var(--ink-2)] p-8 md:p-10 flex flex-col gap-6" data-testid="division-path-partner">
        <Handshake className="w-7 h-7 text-gold" />
        <div>
          <p className="eyebrow">For businesses</p>
          <h2 className="font-display text-3xl md:text-4xl text-slate-50 mt-3 leading-tight">Interested in Working With Us?</h2>
          <p className="text-slate-300 mt-4 leading-relaxed">Are you a dealer, supplier, broker, contractor, manufacturer, or business interested in partnering with Selby Asset Group? We welcome business proposals, contract opportunities, supplier inquiries, and ideas for collaboration.</p>
          <ul className="mt-5 grid sm:grid-cols-2 gap-2 text-sm text-slate-300">
            {PARTNER[d.key].map((p) => <li key={p} className="flex gap-2"><span className="w-1.5 h-1.5 rotate-45 bg-[var(--gold)] mt-2 shrink-0" />{p}</li>)}
          </ul>
        </div>
        <div className="mt-auto flex flex-wrap gap-3">
          <button type="button" onClick={() => onPartner("Business Partnership")} className="btn-gold" data-testid="division-partner-button"><Handshake className="w-4 h-4" /> Partner With Us</button>
          <button type="button" onClick={() => onPartner("Project Proposal")} className="btn-ghost" data-testid="division-proposal-button"><FileText className="w-4 h-4" /> Submit a Proposal</button>
        </div>
      </div>
    </div>
  </section>
);

function DivisionView({ divKey: key }) {
  const [params] = useSearchParams();
  const s = useSettings();
  const d = DIVISIONS.find((x) => x.key === key);
  const preset = params.get("type") || params.get("category") || "";
  const [type, setType] = useState(d?.categories.includes(preset) ? preset : "");
  const [oppCategory, setOppCategory] = useState("");
  const partner = (c) => {
    setOppCategory(c);
    setTimeout(scrollToPartner, 50);
  };
  const { data: facets } = useQuery({ queryKey: ["facets"], queryFn: async () => (await http.get("/assets/facets")).data, retry: 1 });
  if (!d) return <NotFound />;
  const listings = facets?.counts?.[d.key] ?? 0;
  const pick = (c) => {
    setType(c);
    setTimeout(scrollToRequest, 50);
  };

  return (
    <div data-testid={`division-page-${d.key}`}>
      <section className="relative min-h-[72vh] flex items-end overflow-hidden grain">
        <img src={d.img} alt={d.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[linear-gradient(100deg,#0a0d14_10%,rgba(10,13,20,0.7)_55%,rgba(10,13,20,0.3))]" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0a0d14] to-transparent" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 pt-40 pb-16 w-full">
          <Link to="/divisions" className="eyebrow hover:underline">Our Divisions</Link>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl font-medium leading-[0.95] mt-6 text-slate-50">
            <MaskLines lines={[d.name.split(" ")[0], <em key="d" className="text-gold">Division.</em>]} />
          </h1>
          <p className="mt-6 max-w-xl text-base md:text-lg text-slate-200">{d.blurb}</p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button type="button" onClick={scrollToRequest} className="btn-gold" data-testid="division-hero-request-button">Request an Asset <ArrowRight className="w-4 h-4" /></button>
            <AvailableBadge className="bg-[#0a0d14]/60 backdrop-blur" />
          </div>
        </div>
      </section>

      <TwoPaths d={d} onPartner={partner} />

      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-24" data-testid="division-asset-types">
        <Reveal className="grid lg:grid-cols-12 gap-8 items-end mb-12">
          <div className="lg:col-span-7">
            <p className="eyebrow">What we can source</p>
            <h2 className="font-display text-4xl md:text-5xl text-slate-50 leading-[1] mt-4">We Can Help You <em className="text-gold">Source It.</em></h2>
          </div>
          <p className="lg:col-span-5 text-slate-300 leading-relaxed">
            Selby Asset Group helps customers source {d.short.toLowerCase()} assets based on their requirements, preferences and budget.
            {d.key === "marine" && " The Marine Division focuses exclusively on boats and yachts."} Choose an asset type to start a request. Each one is available upon request and subject to market availability.
          </p>
        </Reveal>
        <AssetTypeGrid division={d} selected={type} onSelect={pick} />
      </section>

      {listings > 0 && (
        <section className="border-t hairline" data-testid="division-listings">
          <div className="max-w-7xl mx-auto px-5 md:px-10 pt-16"><p className="eyebrow">Verified {d.short.toLowerCase()} listings</p></div>
          <Inventory fixedDivision={d} />
        </section>
      )}

      <section className="border-t hairline bg-[var(--ink-2)]">
        <div id="request-form" className="scroll-mt-24 max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28 grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-4 space-y-6">
            <p className="eyebrow">{d.name} · Sourcing request</p>
            <h2 className="font-display text-4xl md:text-5xl leading-[1]">Request a {d.short.toLowerCase()} <em>asset.</em></h2>
            <p className="text-slate-300 leading-relaxed">
              {type ? <>You're requesting: <span className="text-gold" data-testid="division-selected-type">{type}</span>. </> : null}
              Share your preferred make and model, year range, budget and specifications, and our team will follow up personally.
            </p>
            <a href={`mailto:${s.sales_email}?subject=${encodeURIComponent(`${d.name} sourcing request`)}`} className="flex items-center gap-3 font-display text-2xl hover:text-gold transition-colors break-all" data-testid="division-sales-email"><Mail className="w-5 h-5 text-gold shrink-0" />{s.sales_email}</a>
            <a href={telHref(s.phone)} className="flex items-center gap-3 font-display text-2xl hover:text-gold transition-colors" data-testid="division-phone"><Phone className="w-5 h-5 text-gold" />{s.phone}</a>
          </Reveal>
          <div className="lg:col-span-8">
            <SourcingForm initialDivision={d.key} initialType={type} scrollOnDone={false} />
          </div>
        </div>
      </section>

      <section className="border-t hairline" data-testid="division-partner-section">
        <div id="partner-form" className="scroll-mt-24 max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28 grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-4 space-y-6">
            <p className="eyebrow">{d.name} · Partnerships & proposals</p>
            <h2 className="font-display text-4xl md:text-5xl leading-[1]">Partner with our {d.short.toLowerCase()} <em>division.</em></h2>
            <p className="text-slate-300 leading-relaxed">Share your partnership idea, contract opportunity, supply offer or project proposal, and attach any supporting documents. Your submission goes to our team for review; receiving it does not mean it has been accepted.</p>
            <Link to="/business-opportunities" className="inline-flex items-center gap-2 text-sm text-gold hover:underline" data-testid="division-opportunities-link">More about business opportunities <ArrowRight className="w-4 h-4" /></Link>
          </Reveal>
          <div className="lg:col-span-8">
            <OpportunityForm category={oppCategory} setCategory={setOppCategory} division={d.name} scrollTarget="partner-form" />
          </div>
        </div>
      </section>
      <DivisionReviews division={d} />
    </div>
  );
}
